import dayjs from "dayjs";
import { resolvePathCriticalLevel } from "./pathLowStock";

const STOCK_INVOICE_TYPES = new Set(["HOMEOPATHY", "ALLOPATHY", "PATHOLOGY"]);
const MEDICINE_INVOICE_TYPES = new Set(["HOMEOPATHY", "ALLOPATHY"]);
const PATHOLOGY_INVOICE_TYPES = new Set(["PATHOLOGY"]);

const firstDefined = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

const toNumber = (value, fallback = 0) => {
  if (value === undefined || value === null || value === "") return fallback;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? fallback : numeric;
};

const normalizeName = (value) => String(value || "").trim().toLowerCase();

export const getSupplierDisplayName = (supplier = {}) =>
  String(
    firstDefined(supplier.name, supplier.supplierName, supplier.supplier_name) || ""
  ).trim();

export const formatSupplierDate = (value) => {
  if (value == null || value === "") return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : String(value);
};

export const formatSupplierMoney = (value) => {
  const numeric = toNumber(value, 0);
  return Number.isFinite(numeric) ? numeric.toFixed(2) : "0.00";
};

const getInvoiceType = (invoice = {}) =>
  String(invoice.invoiceType || invoice.invoice_type || "").toUpperCase();

const isStockInvoice = (invoice = {}) => {
  const type = getInvoiceType(invoice);
  return !type || STOCK_INVOICE_TYPES.has(type);
};

const invoiceMatchesScope = (invoiceType, inventoryScope) => {
  if (!inventoryScope || inventoryScope === "all") return true;
  if (!invoiceType) return true;
  if (inventoryScope === "medicine") return MEDICINE_INVOICE_TYPES.has(invoiceType);
  if (inventoryScope === "pathology") return PATHOLOGY_INVOICE_TYPES.has(invoiceType);
  return true;
};

const mapInventoryBatch = (record = {}, sourceType = "medicine") => {
  const quantity = toNumber(firstDefined(record.quantity, record.qty), 0);
  const unitVendorPrice = toNumber(
    firstDefined(record.vendorPrice, record.vendor_price),
    0
  );
  const storedSystemPrice = toNumber(
    firstDefined(record.price, record.systemPrice),
    0
  );
  // Medicine and pathology stock both store system LINE TOTAL in `price`.
  const systemLineAmount = storedSystemPrice;
  const systemUnitPrice =
    quantity > 0 ? systemLineAmount / quantity : systemLineAmount;
  // Supplier price on add-stock is the line amount entered by the user, not a unit rate.
  const vendorLineAmount = unitVendorPrice;

  return {
    id: record.id,
    name: String(firstDefined(record.name, record.itemName) || "").trim(),
    batchNumber: firstDefined(record.batchNumber, record.batch_number) || "",
    expiryDate: formatSupplierDate(
      firstDefined(record.expirationDate, record.expiryDate, record.expiry)
    ),
    quantity,
    price: systemUnitPrice,
    vendorPrice: unitVendorPrice,
    systemAmount: systemLineAmount,
    vendorAmount: vendorLineAmount,
    manufacturer: firstDefined(record.manufacturer, record.medicineManufacturer) || "",
    criticalLevel: resolvePathCriticalLevel(
      firstDefined(record.criticalLevel, record.critical_level)
    ),
    medicineType: firstDefined(record.medicineType, record.medicine_type) || "",
    sourceType,
    invoiceId: firstDefined(record.invoiceId, record.invoice_id) || null,
  };
};

const groupBatchesByItemName = (batches = []) => {
  const map = new Map();
  batches.forEach((batch) => {
    const key = normalizeName(batch.name) || `item-${batch.id}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        name: batch.name || "Unknown Item",
        sourceType: batch.sourceType,
        manufacturer: batch.manufacturer || "",
        medicineType: batch.medicineType || "",
        batches: [],
        systemAmount: 0,
        vendorAmount: 0,
        quantity: 0,
      });
    }
    const item = map.get(key);
    item.batches.push(batch);
    item.systemAmount += toNumber(batch.systemAmount, 0);
    item.vendorAmount += toNumber(batch.vendorAmount, 0);
    item.quantity += toNumber(batch.quantity, 0);
    if (!item.manufacturer && batch.manufacturer) {
      item.manufacturer = batch.manufacturer;
    }
  });
  return Array.from(map.values()).sort((a, b) =>
    String(a.name).localeCompare(String(b.name))
  );
};

const mergeItemLists = (itemsA = [], itemsB = []) => {
  const seenBatchIds = new Set();
  const allBatches = [
    ...itemsA.flatMap((item) => item.batches || []),
    ...itemsB.flatMap((item) => item.batches || []),
  ].filter((batch) => {
    if (batch?.id == null || batch.id === "") return true;
    const key = String(batch.id);
    if (seenBatchIds.has(key)) return false;
    seenBatchIds.add(key);
    return true;
  });
  if (allBatches.length) return groupBatchesByItemName(allBatches);

  const map = new Map();
  [...itemsA, ...itemsB].forEach((item) => {
    const key = item.key || normalizeName(item.name) || `item-${map.size}`;
    if (!map.has(key)) {
      map.set(key, {
        ...item,
        batches: Array.isArray(item.batches) ? [...item.batches] : [],
      });
      return;
    }
    const existing = map.get(key);
    existing.systemAmount =
      toNumber(existing.systemAmount, 0) + toNumber(item.systemAmount, 0);
    existing.vendorAmount =
      toNumber(existing.vendorAmount, 0) + toNumber(item.vendorAmount, 0);
    existing.quantity =
      toNumber(existing.quantity, 0) + toNumber(item.quantity, 0);
    if (!existing.manufacturer && item.manufacturer) {
      existing.manufacturer = item.manufacturer;
    }
  });
  return Array.from(map.values()).sort((a, b) =>
    String(a.name).localeCompare(String(b.name))
  );
};

const recalculateInvoiceAmounts = (invoice) => {
  const items = Array.isArray(invoice.items) ? invoice.items : [];
  const systemAmountFromItems = items.reduce(
    (sum, item) => sum + toNumber(item.systemAmount, 0),
    0
  );
  const vendorAmountFromItems = items.reduce(
    (sum, item) => sum + toNumber(item.vendorAmount, 0),
    0
  );
  return {
    ...invoice,
    items,
    itemNames: items.map((item) => item.name).filter(Boolean),
    systemAmount: systemAmountFromItems || toNumber(invoice.amount, 0),
    vendorAmount: vendorAmountFromItems || toNumber(invoice.amount, 0),
  };
};

/**
 * Collapse duplicate invoice rows (same invoice number / id) into one row
 * and merge their medicine/item lists for the expanded view.
 */
const mergeInvoices = (listA = [], listB = []) => {
  const map = new Map();

  [...listA, ...listB].forEach((invoice) => {
    const invoiceNumberKey = normalizeName(invoice.invoiceNumber);
    const key = invoiceNumberKey
      ? `num-${invoiceNumberKey}`
      : invoice.id != null
        ? `id-${invoice.id}`
        : `tmp-${map.size}`;

    if (!map.has(key)) {
      map.set(
        key,
        recalculateInvoiceAmounts({
          ...invoice,
          items: Array.isArray(invoice.items) ? [...invoice.items] : [],
        })
      );
      return;
    }

    const existing = map.get(key);
    const mergedItems = mergeItemLists(existing.items, invoice.items);
    const preferNewerDate =
      dayjs(invoice.date).isValid() &&
      (!dayjs(existing.date).isValid() ||
        dayjs(invoice.date).valueOf() >= dayjs(existing.date).valueOf());
    // Same DB row can appear via supplierId and supplierName lookups — don't double-sum.
    // Distinct rows sharing an invoice number (multi-item purchase) should sum.
    const sameRecord =
      existing.id != null &&
      invoice.id != null &&
      String(existing.id) === String(invoice.id);
    const addAmount = (a, b) =>
      sameRecord ? toNumber(a, 0) : toNumber(a, 0) + toNumber(b, 0);

    map.set(
      key,
      recalculateInvoiceAmounts({
        ...existing,
        id: existing.id ?? invoice.id,
        invoiceNumber: existing.invoiceNumber || invoice.invoiceNumber,
        date: preferNewerDate ? invoice.date || existing.date : existing.date,
        invoiceType: existing.invoiceType || invoice.invoiceType,
        amount: addAmount(existing.amount, invoice.amount),
        discount: addAmount(existing.discount, invoice.discount),
        sgst: addAmount(existing.sgst, invoice.sgst),
        cgst: addAmount(existing.cgst, invoice.cgst),
        totalAmount: addAmount(existing.totalAmount, invoice.totalAmount),
        supplierId: existing.supplierId ?? invoice.supplierId,
        supplierName: existing.supplierName || invoice.supplierName,
        items: mergedItems,
      })
    );
  });

  return Array.from(map.values()).sort((a, b) => {
    const dateA = dayjs(a.date);
    const dateB = dayjs(b.date);
    if (dateA.isValid() && dateB.isValid()) return dateB.valueOf() - dateA.valueOf();
    return String(b.invoiceNumber || "").localeCompare(String(a.invoiceNumber || ""));
  });
};

/**
 * Build supplier → stock invoices → medicines/items → batches hierarchy
 * from suppliers, invoices, medicine inventory and pathology inventory.
 *
 * @param {object} options
 * @param {"medicine"|"pathology"|"all"} [options.inventoryScope="all"]
 *   When "medicine", only medicine purchase suppliers/invoices/items are included.
 *   When "pathology", only pathology inventory suppliers/invoices/items are included.
 */
export function buildSupplierInvoiceHierarchy({
  suppliers = [],
  invoices = [],
  medicineInventory = [],
  pathInventory = [],
  inventoryScope = "all",
} = {}) {
  const includeMedicine =
    inventoryScope === "all" || inventoryScope === "medicine";
  const includePathology =
    inventoryScope === "all" || inventoryScope === "pathology";

  const medicineBatches = includeMedicine
    ? (Array.isArray(medicineInventory) ? medicineInventory : []).map((row) =>
        mapInventoryBatch(row, "medicine")
      )
    : [];
  const pathBatches = includePathology
    ? (Array.isArray(pathInventory) ? pathInventory : []).map((row) =>
        mapInventoryBatch(row, "pathology")
      )
    : [];
  const allBatches = [...medicineBatches, ...pathBatches];

  const batchesByInvoiceId = allBatches.reduce((acc, batch) => {
    if (batch.invoiceId == null || batch.invoiceId === "") return acc;
    const key = String(batch.invoiceId);
    if (!acc[key]) acc[key] = [];
    acc[key].push(batch);
    return acc;
  }, {});

  const stockInvoices = (Array.isArray(invoices) ? invoices : []).filter(
    (invoice) =>
      isStockInvoice(invoice) &&
      invoiceMatchesScope(getInvoiceType(invoice), inventoryScope)
  );

  const invoicesBySupplierId = {};
  const invoicesBySupplierName = {};

  stockInvoices.forEach((invoice) => {
    const invoiceId = firstDefined(invoice.id, invoice.invoiceId);
    const linkedBatches =
      invoiceId != null ? batchesByInvoiceId[String(invoiceId)] || [] : [];
    const items = groupBatchesByItemName(linkedBatches);
    const invoiceType = getInvoiceType(invoice);

    // Scoped views: keep invoices that match type, or that have linked stock items.
    // Drop cross-type leftovers that somehow have no relevant items.
    if (
      inventoryScope !== "all" &&
      !items.length &&
      invoiceType &&
      !invoiceMatchesScope(invoiceType, inventoryScope)
    ) {
      return;
    }
    if (inventoryScope !== "all" && !items.length && !invoiceType) {
      return;
    }

    const enriched = recalculateInvoiceAmounts({
      id: invoiceId,
      invoiceNumber:
        firstDefined(invoice.invoiceNumber, invoice.invoice_number) || "",
      date: formatSupplierDate(
        firstDefined(invoice.date, invoice.invoiceDate, invoice.invoice_date)
      ),
      invoiceType,
      amount: toNumber(invoice.amount, 0),
      discount: toNumber(invoice.discount, 0),
      sgst: toNumber(invoice.sgst, 0),
      cgst: toNumber(invoice.cgst, 0),
      totalAmount: toNumber(
        firstDefined(invoice.totalAmount, invoice.total_amount),
        0
      ),
      supplierId: firstDefined(invoice.supplierId, invoice.supplier_id),
      supplierName: String(
        firstDefined(
          invoice.supplierName,
          invoice.supplier_name,
          invoice.fromName,
          invoice.from_name
        ) || ""
      ).trim(),
      items,
    });

    if (enriched.supplierId != null && enriched.supplierId !== "") {
      const idKey = String(enriched.supplierId);
      if (!invoicesBySupplierId[idKey]) invoicesBySupplierId[idKey] = [];
      invoicesBySupplierId[idKey].push(enriched);
    }

    const nameKey = normalizeName(enriched.supplierName);
    if (nameKey) {
      if (!invoicesBySupplierName[nameKey]) invoicesBySupplierName[nameKey] = [];
      invoicesBySupplierName[nameKey].push(enriched);
    }
  });

  return (Array.isArray(suppliers) ? suppliers : [])
    .map((supplier) => {
      const displayName = getSupplierDisplayName(supplier);
      const byId =
        supplier.id != null ? invoicesBySupplierId[String(supplier.id)] || [] : [];
      const byName = invoicesBySupplierName[normalizeName(displayName)] || [];
      const supplierInvoices = mergeInvoices(byId, byName);

      return {
        ...supplier,
        displayName,
        invoices: supplierInvoices,
        invoiceCount: supplierInvoices.length,
        itemCount: supplierInvoices.reduce(
          (sum, invoice) => sum + (invoice.items?.length || 0),
          0
        ),
      };
    })
    .filter((supplier) => {
      // Scoped tabs only list suppliers that purchased that inventory type.
      if (inventoryScope === "all") return true;
      return supplier.invoiceCount > 0;
    })
    .sort((a, b) => (b.id || 0) - (a.id || 0));
}

export function supplierMatchesFilters(supplier, filters = {}) {
  const {
    searchQuery = "",
    filterId = "",
    filterName = "",
    filterAddress = "",
    filterContact = "",
    filterGstNumber = "",
    filterInvoiceNumber = "",
    filterItemName = "",
    filterBatchNumber = "",
    filterDateFrom = "",
    filterDateTo = "",
  } = filters;

  const invoices = Array.isArray(supplier.invoices) ? supplier.invoices : [];
  const allItems = invoices.flatMap((invoice) => invoice.items || []);
  const allBatches = allItems.flatMap((item) => item.batches || []);

  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    const vals = [
      supplier.id,
      supplier.displayName,
      supplier.name,
      supplier.supplierName,
      supplier.address,
      supplier.contact,
      supplier.gstNumber,
      ...invoices.flatMap((invoice) => [
        invoice.invoiceNumber,
        invoice.date,
        invoice.invoiceType,
        invoice.systemAmount,
        invoice.vendorAmount,
        invoice.totalAmount,
        ...(invoice.itemNames || []),
      ]),
      ...allBatches.flatMap((batch) => [
        batch.name,
        batch.batchNumber,
        batch.expiryDate,
        batch.manufacturer,
        batch.quantity,
        batch.criticalLevel,
      ]),
    ]
      .filter((v) => v != null && v !== "")
      .map((v) => String(v).toLowerCase());
    if (!vals.some((v) => v.includes(q))) return false;
  }

  if (filterId.trim()) {
    if (!String(supplier.id || "").toLowerCase().includes(filterId.toLowerCase())) {
      return false;
    }
  }
  if (filterName.trim()) {
    const name = String(supplier.displayName || "").toLowerCase();
    if (!name.includes(filterName.toLowerCase())) return false;
  }
  if (filterAddress.trim()) {
    if (
      !String(supplier.address || "")
        .toLowerCase()
        .includes(filterAddress.toLowerCase())
    ) {
      return false;
    }
  }
  if (filterContact.trim()) {
    if (
      !String(supplier.contact || "")
        .toLowerCase()
        .includes(filterContact.toLowerCase())
    ) {
      return false;
    }
  }
  if (filterGstNumber.trim()) {
    if (
      !String(supplier.gstNumber || "")
        .toLowerCase()
        .includes(filterGstNumber.toLowerCase())
    ) {
      return false;
    }
  }
  if (filterInvoiceNumber.trim()) {
    const q = filterInvoiceNumber.toLowerCase();
    if (
      !invoices.some((invoice) =>
        String(invoice.invoiceNumber || "")
          .toLowerCase()
          .includes(q)
      )
    ) {
      return false;
    }
  }
  if (filterItemName.trim()) {
    const q = filterItemName.toLowerCase();
    if (
      !allItems.some((item) =>
        String(item.name || "")
          .toLowerCase()
          .includes(q)
      )
    ) {
      return false;
    }
  }
  if (filterBatchNumber.trim()) {
    const q = filterBatchNumber.toLowerCase();
    if (
      !allBatches.some((batch) =>
        String(batch.batchNumber || "")
          .toLowerCase()
          .includes(q)
      )
    ) {
      return false;
    }
  }
  if (filterDateFrom) {
    const from = dayjs(filterDateFrom);
    if (from.isValid()) {
      const hasDate = invoices.some((invoice) => {
        const date = dayjs(invoice.date);
        return date.isValid() && !date.isBefore(from, "day");
      });
      if (!hasDate) return false;
    }
  }
  if (filterDateTo) {
    const to = dayjs(filterDateTo);
    if (to.isValid()) {
      const hasDate = invoices.some((invoice) => {
        const date = dayjs(invoice.date);
        return date.isValid() && !date.isAfter(to, "day");
      });
      if (!hasDate) return false;
    }
  }

  return true;
}

/** One table row per inventory batch (batch/expiry inline on medicine line). */
export function flattenSupplierInvoiceItems(items = []) {
  const lines = [];
  (Array.isArray(items) ? items : []).forEach((item, itemIndex) => {
    const batches = Array.isArray(item.batches) ? item.batches : [];
    const baseKey = item.key || normalizeName(item.name) || `item-${itemIndex}`;

    if (!batches.length) {
      const qty = toNumber(item.quantity, 0);
      lines.push({
        key: `${baseKey}-line`,
        name: item.name || "Unknown Item",
        sourceType: item.sourceType,
        medicineType: item.medicineType || "",
        manufacturer: item.manufacturer || "",
        quantity: qty,
        batchNumber: "",
        expiryDate: "",
        criticalLevel: item.criticalLevel ?? 0,
        costPerUnit:
          qty > 0 ? toNumber(item.systemAmount, 0) / qty : toNumber(item.systemAmount, 0),
        systemAmount: toNumber(item.systemAmount, 0),
        vendorAmount: toNumber(item.vendorAmount, 0),
      });
      return;
    }

    batches.forEach((batch, batchIndex) => {
      lines.push({
        key: `${baseKey}-batch-${batch.id ?? batchIndex}`,
        name: batch.name || item.name || "Unknown Item",
        sourceType: batch.sourceType || item.sourceType,
        medicineType: batch.medicineType || item.medicineType || "",
        manufacturer: batch.manufacturer || item.manufacturer || "",
        quantity: toNumber(batch.quantity, 0),
        batchNumber: batch.batchNumber || "",
        expiryDate: batch.expiryDate || "",
        criticalLevel: batch.criticalLevel ?? 0,
        costPerUnit: toNumber(batch.price, 0),
        systemAmount: toNumber(batch.systemAmount, 0),
        vendorAmount: toNumber(batch.vendorAmount, 0),
      });
    });
  });
  return lines;
}

export function buildSupplierDetailsExportSheets(
  suppliers = [],
  { inventoryScope = "medicine" } = {}
) {
  const isPathology = inventoryScope === "pathology";
  const supplierHeaders = [
    "Supplier ID",
    "Supplier Name",
    "Address",
    "Contact",
    "GST Number",
    "Invoices",
  ];

  // Medicine: Medicine Name / Medicine Type. Pathology: Item Name / Item Type.
  const detailHeaders = isPathology
    ? [
        "Invoice Number",
        "Invoice Date",
        "Invoice Type",
        "Item Name",
        "Item Type",
        "Quantity",
        "Cost Per Unit",
        "System Amount",
        "Supplier Amount",
        "Batch Number",
        "Expiry",
        "Critical Level",
        "Batch Quantity",
        "Paid Amount",
      ]
    : [
        "Invoice Number",
        "Invoice Date",
        "Invoice Type",
        "Medicine Name",
        "Medicine Type",
        "Manufacturer",
        "Quantity",
        "Cost Per Unit",
        "System Amount",
        "Supplier Amount",
        "Batch Number",
        "Expiry",
        "Batch Quantity",
        "Paid Amount",
      ];

  const detailTitle = isPathology
    ? "Invoices & Path Items Details"
    : "Invoices & Medicines Details";

  const emptyDetailCols = detailHeaders.length;
  const blank = (count = emptyDetailCols) => Array.from({ length: count }, () => "");

  const buildInvoiceHeaderRow = (invoice) => {
    const row = blank();
    row[0] = invoice.invoiceNumber || "";
    row[1] = invoice.date || "";
    row[2] = invoice.invoiceType || "";
    row[emptyDetailCols - 1] = formatSupplierMoney(invoice.totalAmount);
    return row;
  };

  const buildItemBatchRow = (item, batch = null) => {
    const row = blank();
    const itemType =
      item.sourceType === "pathology"
        ? "Pathology"
        : item.medicineType || "Medicine";
    const manufacturer = batch
      ? batch.manufacturer || item.manufacturer || ""
      : item.manufacturer || "";
    const batchNumber = batch?.batchNumber || "";
    const expiry = batch?.expiryDate || "";
    const batchQty = batch ? batch.quantity ?? 0 : item.quantity ?? 0;
    const costPerUnit = batch
      ? formatSupplierMoney(batch.price)
      : item.quantity > 0
        ? formatSupplierMoney(toNumber(item.systemAmount, 0) / item.quantity)
        : formatSupplierMoney(item.systemAmount);
    const systemLineAmount = batch
      ? formatSupplierMoney(batch.systemAmount)
      : formatSupplierMoney(item.systemAmount);
    const supplierLineAmount = batch
      ? formatSupplierMoney(batch.vendorAmount)
      : formatSupplierMoney(item.vendorAmount);
    const criticalLevel = batch ? batch.criticalLevel ?? 0 : item.criticalLevel ?? "";

    if (isPathology) {
      row[3] = item.name || batch?.name || "";
      row[4] = itemType;
      row[5] = batchQty;
      row[6] = costPerUnit;
      row[7] = systemLineAmount;
      row[8] = supplierLineAmount;
      row[9] = batchNumber;
      row[10] = expiry;
      row[11] = criticalLevel;
      row[12] = batchQty;
    } else {
      row[3] = item.name || batch?.name || "";
      row[4] = itemType;
      row[5] = manufacturer;
      row[6] = batchQty;
      row[7] = costPerUnit;
      row[8] = systemLineAmount;
      row[9] = supplierLineAmount;
      row[10] = batchNumber;
      row[11] = expiry;
      row[12] = batchQty;
    }
    return row;
  };

  const sanitizeExcelSheetName = (value) =>
    String(value || "Sheet")
      .replace(/[\\/?*[\]:]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 31) || "Sheet";

  const usedSheetNames = new Set(["suppliers"]);
  const makeUniqueSheetName = (rawName) => {
    let name = sanitizeExcelSheetName(rawName);
    if (!usedSheetNames.has(name.toLowerCase())) {
      usedSheetNames.add(name.toLowerCase());
      return name;
    }
    let index = 2;
    while (index < 1000) {
      const suffix = `-${index}`;
      const candidate = `${name.slice(0, Math.max(31 - suffix.length, 1))}${suffix}`;
      if (!usedSheetNames.has(candidate.toLowerCase())) {
        usedSheetNames.add(candidate.toLowerCase());
        return candidate;
      }
      index += 1;
    }
    return name;
  };

  const supplierRows = [];
  const supplierDetailSheets = [];

  (Array.isArray(suppliers) ? suppliers : []).forEach((supplier) => {
    const supplierName =
      supplier.displayName || getSupplierDisplayName(supplier);
    const invoices = Array.isArray(supplier.invoices) ? supplier.invoices : [];

    supplierRows.push([
      supplier.id ?? "",
      supplierName,
      supplier.address || "",
      supplier.contact || "",
      supplier.gstNumber || "",
      supplier.invoiceCount ?? invoices.length,
    ]);

    const detailRows = [];
    invoices.forEach((invoice) => {
      const items = Array.isArray(invoice.items) ? invoice.items : [];
      // One invoice header row, then nested medicine/path item (+ batch) rows.
      detailRows.push(buildInvoiceHeaderRow(invoice));

      if (!items.length) return;

      items.forEach((item) => {
        const batches = Array.isArray(item.batches) ? item.batches : [];
        if (!batches.length) {
          detailRows.push(buildItemBatchRow(item));
          return;
        }
        batches.forEach((batch) => {
          detailRows.push(buildItemBatchRow(item, batch));
        });
      });
    });

    const sheetLabel = `${supplierName || "Supplier"}-ID-${supplier.id ?? ""}`;
    supplierDetailSheets.push({
      sheetName: makeUniqueSheetName(sheetLabel),
      // Match sample title without org prefix on detail sheets.
      organizationName: "",
      title: detailTitle,
      tableHeaders: detailHeaders,
      tableRows: detailRows,
    });
  });

  return {
    sheets: [
      {
        sheetName: "Suppliers",
        title: "Supplier Details",
        tableHeaders: supplierHeaders,
        tableRows: supplierRows,
      },
      ...supplierDetailSheets,
    ],
  };
}
